import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {BookOfShadowsGameModuleConfig} from "../../BookOfShadowsGameModuleConfig";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {BetLineEvent} from "@nolimitcity/slot-game/bin/core/betline/event/BetLineEvent";
import {IBetLineWinData, IBetWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {BookOfShadowsGameConfig} from "../../BookOfShadowsGameConfig";
import {BetLineWin} from "@nolimitcity/slot-game/bin/timelinegameround/game/gameround/presentations/BetLineWin";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {BookOfShadowsParsedInitData} from "../../server/data/BookOfShadowsParsedInitData";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {StateMatrix} from "../../utils/StateMatrix";
import {SymbolUtil} from "../../utils/SymbolUtil";

export class BookOfShadowsBetLineWin extends BetLineWin {
    private _symbolStates: StateMatrix<string>;

    /**
     * @inheritDoc
     * @param data
     */
    public prepare(data: ParsedGameData): void {
        this._symbolStates = SymbolUtil.createSymbolStateMatrixFromBetWins(
            <IBetLineWinData[]>data.betWins!,
            BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!.keyword,
            BookOfShadowsGameConfig.instance.SYMBOL_STATES.win!.keyword);
    }

    /**
     * @inheritDoc
     * @param data
     */
    public getAnimation(data: ParsedGameData | BookOfShadowsParsedInitData): Timeline | null {
        if (data.betWins === undefined || data.betWins.length <= 0 || data.nextReelSetName === "FREESPIN_REELSET") {
            return null;
        }

        const tl: TimelineLite = new TimelineLite(),
            noWinAnimations: TimelineLite = this.getNoWinSymbolAnimations(),
            winAnimations: TimelineLite = this.getWinAnimationsBOS(data.betWins);

        tl.add([
            noWinAnimations,
            winAnimations,
        ]);

        return tl;
    }

    /**
     * @inheritDoc
     */
    public abort(): gsap.TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_ALL_BET_LINES));
            BookOfShadowsGameModuleConfig.betLineWinAmountView.hide();
            BookOfShadowsGameModuleConfig.symWinAnims.stopAllAnimation();

            for (let i: number = 0; i < BookOfShadowsGameConfig.instance.REELS_NUM; i++) {
                for (let j: number = 0; j < BookOfShadowsGameConfig.instance.SYMBOLS_NUM_IN_REEL[2]; j++) {
                    this.changeStateOfSymbols(i, j, BookOfShadowsGameConfig.instance.SYMBOL_STATES.normal);
                }
            }
        }, 0);

        return tl;
    }

    /**
     * @description Need this method to play symbol animations.
     * @param reelId
     * @param symbolId
     * @param state
     */
    protected getSymbolAnimationBOS(reelId: number, symbolId: number, state: ISymbolStateConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symbol: StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if (symbol) {
            // difference from parent method is here.
            const symbolAnimation: Timeline = BookOfShadowsGameModuleConfig.symWinAnims.playAnimation(reelId, symbolId, symbol, true);

            tl.add(symbolAnimation);
        }
        return tl;
    }

    /**
     * @inheritDoc => similar method is in parent class
     * @param data
     */
    private getWinAnimationsBOS(data: IBetWinData[]): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let betLineWinData: IBetLineWinData,
            reelId: number,
            symId: number;

        for (let i: number = 0; i < data.length; i++) {
            tl.add([
                () => {
                    betLineWinData = <IBetLineWinData>data[i];
                    reelId = ((betLineWinData.nrOfSymbols === 2) ? 1 : 2);
                    symId = ((betLineWinData.nrOfSymbols === 2) ? betLineWinData.betLine[1] : betLineWinData.betLine[2]);

                    EventHandler.dispatchEvent(new GameEvent(BetLineEvent.SHOW_WIN_BET_LINE, (betLineWinData).betLineIndex + 1));
                    BookOfShadowsGameModuleConfig.betLineWinAmountView.show(betLineWinData.amount, reelId, symId);
                },
                this.getLineAnimationBOS(<IBetLineWinData>data[i], data.length),
            ]);
        }
        tl.add(() => BookOfShadowsGameModuleConfig.betLineWinAmountView.hide());
        return tl;
    }

    /**
     * @description Controlling the animation time and hiding the betLine from this method.
     * @param betLineWinData
     * @param length
     */
    private getLineAnimationBOS(betLineWinData: IBetLineWinData, length: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            for (let reelId: number = 0; reelId < betLineWinData.nrOfSymbols; reelId++) {
                this.getSymbolAnimationBOS(reelId, betLineWinData.betLine[reelId], BookOfShadowsGameConfig.instance.SYMBOL_STATES.win);
            }
        });
        // need to add this empty tween so that the symbol animation play full for its time.
        tl.add(new TweenLite(() => {
        }, 0.9, {}), 0);

        if (length !== 1) {
            tl.add(() => {
                EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_WIN_BET_LINE, betLineWinData.betLineIndex + 1));

                for (let reelId: number = 0; reelId < betLineWinData.nrOfSymbols; reelId++) {
                    this.changeStateOfSymbols(reelId, betLineWinData.betLine[reelId], BookOfShadowsGameConfig.instance.SYMBOL_STATES.normal);
                }
            });

            // need to add this empty tween so that the symbol set back to its normal state between the toggling.
            tl.add(new TweenLite(() => {
            }, 0.1, {}), 0.9);
        }

        return tl;
    }

    /**
     * @description To change the state of symbols.
     * @param reelId
     * @param symbolId
     * @param state
     */
    private changeStateOfSymbols(reelId: number, symbolId: number, state: ISymbolStateConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const symbol: StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if (symbol) {
            // #189
            if (state.keyword === "noWin" && symbol.symName === "S") {
                return tl;
            }

            const symbolAnimation: Timeline = symbol.changeState({
                state: state,
                setStack: true,
                fadeInDuration: 0.1
            })!;

            tl.add(symbolAnimation);
        }

        return tl;
    }

    /**
     * @description This method will set the no win state.
     */
    private getNoWinSymbolAnimations(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            for (let reelId = 0; reelId < BookOfShadowsGameConfig.instance.REELS_NUM; reelId++) {
                const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!.keyword);
                for (let coords of reelCoords) {
                    this.changeStateOfSymbols(coords[0], coords[1], BookOfShadowsGameConfig.instance.SYMBOL_STATES.noWin!);
                }
            }
        }, 0);

        return tl;
    }
}